import {
  FOLHA,
  LAYOUTS,
  aplicarEmTodas,
  enquadramentoInicial,
  faixasDaFolha,
  tipoDoEspaco,
  type Camada,
  type Enquadramento,
  type FolhaDeImpressao,
  type FonteDaFoto,
  type LayoutId,
  type Tamanho,
} from "@foto/shared";
import { semTextosEmBranco } from "../camadas/camadas-da-celula.js";
import {
  camadasIndisponiveis,
  idsDasFigurinhas,
  type FigurinhasDasCamadas,
} from "../camadas/figurinhas-das-camadas.js";

/**
 * O estado das folhas na tela de impressão, sem React.
 *
 * Separado do componente pelo mesmo motivo da fila da galeria: são regras — que
 * formato escolher para quantas fotos, o que acontece com as fotos ao trocar de
 * formato, quando o enquadramento volta ao padrão —, e regra se testa sem
 * navegador. O Vitest do projeto só roda módulo puro no web.
 */

/**
 * A ampliação de cenário (IA) que a célula usa no lugar da foto publicada.
 * `tela` é o endereço para a prévia; o servidor lê o arquivo pelo `id`.
 */
export interface AmpliacaoDaCelula {
  id: number;
  tela: string;
  tamanho: Tamanho;
  /** A conferência desconfiou: a tela pede para o operador conferir. */
  aviso?: string | null;
}

/** Uma foto publicada como a bandeja a mostra. */
export interface FotoNaBandeja {
  id: number;
  nome: string;
  /** A peça publicada: a capa da vivência e o retrato, ou, sem capa, a própria foto. */
  mini: string;
  tela: string;
  /** A foto que a IA gerou, sem capa — só quando é uma versão à parte da peça. */
  foto?: { mini: string; tela: string };
  /** A foto que o operador tirou — só nas feitas com IA; na foto sem IA, a peça já é ela. */
  original?: { mini: string; tela: string };
}

export interface CelulaDaFolha {
  fotoId: number;
  /** A versão da foto; ausente é a peça publicada. */
  fonte?: FonteDaFoto;
  enquadramento: Enquadramento;
  ampliada?: AmpliacaoDaCelula;
  /**
   * As figurinhas, logos e textos desta foto, de baixo para cima (o fim é a frente). **Ausente quando não há
   * nenhuma** — nunca `[]` —, para a célula sem camada ser exatamente a de antes e o pedido não mudar de forma.
   * São da célula, e não da foto: trocar a foto mantém as camadas (o operador troca de foto e quer o mesmo
   * título), mas o enquadramento, que é por foto, volta ao padrão.
   */
  camadas?: Camada[];
}

/** A faixa de um marcador pronto num espaço de marcador da folha mista (#115). */
export interface MarcadorNaFolha {
  marcadorId: number;
  /** Qual das três faixas do conjunto: 0, 1 ou 2. */
  faixa: number;
}

export interface Folha {
  /** Só distingue uma folha da outra na tela; o servidor não o vê. */
  id: number;
  layout: LayoutId;
  /**
   * Uma entrada por célula do formato; `null` é célula vazia. No espaço de marcador é sempre `null`: a foto não vai
   * ali, e o que vai fica em `marcadores`.
   */
  celulas: (CelulaDaFolha | null)[];
  /** As faixas de marcador, pelo índice do espaço: só nos formatos mistos (#115). */
  marcadores?: Record<number, MarcadorNaFolha>;
}

/** O espaço `indice` do formato é de marcador? */
export const ehEspacoDeMarcador = (layout: LayoutId, indice: number): boolean =>
  tipoDoEspaco(layout, indice) === "marcador";

/**
 * O tamanho de cada imagem (pixels de uma versão dela), por foto **e versão**: a peça
 * é 1080 × 1920, a foto da IA é quadrada e a original tem a proporção da câmera, e o
 * enquadramento depende da que a célula usa.
 */
export type Tamanhos = Record<string, Tamanho>;

export const chaveDaFoto = (fotoId: number, fonte?: FonteDaFoto): string =>
  `${fotoId}:${fonte ?? "peca"}`;

/**
 * A versão que um toque na bandeja coloca: a foto da IA ou a original só quando foi
 * pedida **e** a foto a tem. Sem a versão à parte, a peça (ausente quer dizer peça).
 */
export function fonteParaColocar(
  foto: FotoNaBandeja | undefined,
  desejada: FonteDaFoto,
): FonteDaFoto | undefined {
  if (desejada === "foto" && foto?.foto) return "foto";
  if (desejada === "original" && foto?.original) return "original";
  return undefined;
}

/** A imagem de tela de uma versão da foto: a da versão, se a foto a tem; senão a peça. */
export function telaDaVersao(foto: FotoNaBandeja, fonte: FonteDaFoto | undefined): string {
  if (fonte === "foto" && foto.foto) return foto.foto.tela;
  if (fonte === "original" && foto.original) return foto.original.tela;
  return foto.tela;
}

/** A imagem que a célula mostra: a ampliação, ou a versão da foto que ela usa. */
export function telaDaCelula(
  celula: CelulaDaFolha,
  foto: FotoNaBandeja | undefined,
): string | undefined {
  if (celula.ampliada) return celula.ampliada.tela;
  return foto ? telaDaVersao(foto, celula.fonte) : undefined;
}

export function folhaVazia(id: number, layout: LayoutId): Folha {
  return { id, layout, celulas: LAYOUTS[layout].celulas.map(() => null) };
}

/** Largura e altura da célula, em mm: só a proporção importa para o enquadramento. */
export function tamanhoDaCelula(layout: LayoutId, indice: number): Tamanho {
  const c = LAYOUTS[layout].celulas[indice]!;
  return { largura: c.largura, altura: c.altura };
}

/**
 * Uma célula com a foto no enquadramento de partida. Sem o tamanho da foto
 * (ainda carregando), cai em "mostrar a largura toda", que a prévia corrige
 * quando o tamanho chega.
 */
export function celulaCom(
  fotoId: number,
  layout: LayoutId,
  indice: number,
  tamanhos: Tamanhos,
  modo: "preencher" | "encaixar" = "preencher",
  fonte?: FonteDaFoto,
  camadas?: Camada[],
): CelulaDaFolha {
  const foto = tamanhos[chaveDaFoto(fotoId, fonte)];
  const enquadramento = foto
    ? enquadramentoInicial(foto, tamanhoDaCelula(layout, indice), modo)
    : { x: 0, y: 0, largura: 100 };
  return {
    fotoId,
    ...(fonte ? { fonte } : {}),
    enquadramento,
    ...(camadas && camadas.length > 0 ? { camadas } : {}),
  };
}

export function colocarFoto(
  folha: Folha,
  indice: number,
  fotoId: number,
  tamanhos: Tamanhos,
  fonte?: FonteDaFoto,
): Folha {
  // Espaço de marcador não recebe foto: a faixa tem outra proporção, e o servidor recusaria.
  if (ehEspacoDeMarcador(folha.layout, indice)) return folha;
  const celulas = [...folha.celulas];
  // As camadas ficam: o enquadramento é da foto e volta ao padrão, mas o título e as figurinhas são da célula.
  celulas[indice] = celulaCom(
    fotoId,
    folha.layout,
    indice,
    tamanhos,
    "preencher",
    fonte,
    folha.celulas[indice]?.camadas,
  );
  return { ...folha, celulas };
}

/**
 * A célula passa a usar outra versão da foto. É como trocar de foto: as imagens têm
 * outra proporção, então o enquadramento volta ao padrão e a ampliação de cenário
 * (feita sobre a versão antiga) sai.
 */
export function trocarFonte(
  folha: Folha,
  indice: number,
  fonte: FonteDaFoto | undefined,
  tamanhos: Tamanhos,
): Folha {
  const atual = folha.celulas[indice];
  if (!atual || atual.fonte === fonte) return folha;
  return colocarFoto(folha, indice, atual.fotoId, tamanhos, fonte);
}

export function esvaziarCelula(folha: Folha, indice: number): Folha {
  const celulas = [...folha.celulas];
  celulas[indice] = null;
  if (folha.marcadores?.[indice] === undefined) return { ...folha, celulas };
  const { [indice]: _tirado, ...marcadores } = folha.marcadores;
  return { ...folha, celulas, marcadores };
}

/** Põe a faixa de um marcador pronto no espaço de marcador `indice`. Em espaço de foto, nada muda. */
export function colocarMarcador(folha: Folha, indice: number, marcador: MarcadorNaFolha): Folha {
  if (!ehEspacoDeMarcador(folha.layout, indice)) return folha;
  return { ...folha, marcadores: { ...folha.marcadores, [indice]: { ...marcador } } };
}

/** Quantas faixas de marcador a folha leva. */
export const quantosMarcadores = (folha: Folha): number =>
  Object.keys(folha.marcadores ?? {}).length;

/** Tudo o que a folha leva, fotos e marcadores: com zero, não há o que imprimir. */
export const quantoTemNaFolha = (folha: Folha): number =>
  quantasFotos(folha) + quantosMarcadores(folha);

/**
 * Onde a faixa `faixa` está na imagem da folha de marcadores do conjunto (`folha.tela`), como fundo CSS: a tela
 * mostra a faixa recortada da folha pronta, sem pedir ao servidor uma imagem por faixa. As porcentagens são as do
 * `background-position` — a posição do ponto da imagem que coincide com o mesmo ponto da caixa.
 */
export function recorteDaFaixa(faixa: number): {
  backgroundSize: string;
  backgroundPosition: string;
} {
  const f = faixasDaFolha()[faixa] ?? faixasDaFolha()[0]!;
  const posicao = (inicio: number, tamanho: number, total: number) =>
    total === tamanho ? 0 : (inicio / (total - tamanho)) * 100;
  return {
    backgroundSize: `${(FOLHA.larguraPx / f.largura) * 100}% ${(FOLHA.alturaPx / f.altura) * 100}%`,
    backgroundPosition: `${posicao(f.x, f.largura, FOLHA.larguraPx)}% ${posicao(f.y, f.altura, FOLHA.alturaPx)}%`,
  };
}

export function guardarEnquadramento(
  folha: Folha,
  indice: number,
  enquadramento: Enquadramento,
): Folha {
  const atual = folha.celulas[indice];
  if (!atual) return folha;
  const celulas = [...folha.celulas];
  celulas[indice] = { ...atual, enquadramento };
  return { ...folha, celulas };
}

/**
 * A foto de uma célula em todas as outras, com o mesmo enquadramento: é o caso
 * da foto 3 × 4, repetida para encher a folha. Todas as células de um formato
 * têm o mesmo tamanho, então o enquadramento serve em qualquer uma.
 */
export function repetirEmTodas(folha: Folha, indice: number): Folha {
  const origem = folha.celulas[indice];
  if (!origem) return folha;
  return {
    ...folha,
    // Só nos espaços de foto: o de marcador fica com a faixa dele.
    celulas: folha.celulas.map((_, i) =>
      ehEspacoDeMarcador(folha.layout, i) ? null : { ...origem },
    ),
  };
}

/**
 * Trocar de formato mantém as fotos, na mesma ordem, até onde couberem. O
 * enquadramento volta ao padrão: a célula nova tem outra proporção, e o recorte
 * de uma não serve na outra.
 */
export function trocarLayout(folha: Folha, layout: LayoutId, tamanhos: Tamanhos): Folha {
  const fotos = folha.celulas.flatMap((c) => (c ? [c] : []));
  const faixas = Object.entries(folha.marcadores ?? {})
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, m]) => m);
  const nova = folhaVazia(folha.id, layout);

  // As fotos vão, na ordem, para os espaços de foto; as faixas de marcador, para os de marcador (#115).
  let proximaFoto = 0;
  let proximaFaixa = 0;
  const marcadores: Record<number, MarcadorNaFolha> = {};
  const celulas = nova.celulas.map((_, i) => {
    if (ehEspacoDeMarcador(layout, i)) {
      const m = faixas[proximaFaixa++];
      if (m) marcadores[i] = m;
      return null;
    }
    const f = fotos[proximaFoto++];
    return f !== undefined
      ? celulaCom(
          f.fotoId,
          layout,
          i,
          tamanhos,
          "preencher",
          f.fonte,
          // As camadas são frações da célula: seguem para o formato novo na mesma posição relativa.
          f.camadas,
        )
      : null;
  });
  return { ...nova, celulas, ...(Object.keys(marcadores).length > 0 ? { marcadores } : {}) };
}

/** A primeira célula vazia a partir de `de` (e, se não houver, antes dela). */
export function proximaCelulaVazia(folha: Folha, de = 0): number | null {
  const n = folha.celulas.length;
  for (let k = 0; k < n; k++) {
    const i = (de + k) % n;
    // A bandeja põe foto: o espaço de marcador não conta como vazio para ela.
    if (folha.celulas[i] === null && !ehEspacoDeMarcador(folha.layout, i)) return i;
  }
  return null;
}

/** O formato que serve a essa quantidade de fotos, quando ninguém escolheu. */
export function layoutParaQuantidade(quantidade: number): LayoutId {
  if (quantidade <= 1) return "cheia";
  if (quantidade <= 4) return "quatro";
  return "nove-3x4";
}

/**
 * Reparte as fotos em folhas: o formato certo para a quantidade, e o que não
 * coube vai para a folha seguinte — "várias fotos selecionadas numa mesma
 * página", sem obrigar ninguém a montar uma folha por vez.
 */
export function distribuir(
  fotoIds: number[],
  tamanhos: Tamanhos,
  primeiroId = 1,
  fonte?: FonteDaFoto,
): Folha[] {
  const layout = layoutParaQuantidade(fotoIds.length);
  const porFolha = LAYOUTS[layout].celulas.length;
  const folhas: Folha[] = [];

  for (let inicio = 0; inicio < fotoIds.length; inicio += porFolha) {
    let folha = folhaVazia(primeiroId + folhas.length, layout);
    fotoIds.slice(inicio, inicio + porFolha).forEach((id, i) => {
      folha = colocarFoto(folha, i, id, tamanhos, fonte);
    });
    folhas.push(folha);
  }
  return folhas.length > 0 ? folhas : [folhaVazia(primeiroId, "cheia")];
}

/**
 * O pedido que o servidor entende. Tudo que monta a folha — "Baixar", "Ver como vai sair", "Imprimir" — passa por
 * aqui, então a prévia e o papel levam as mesmas camadas.
 *
 * `camadas` só vai quando há (a célula sem camada sai exatamente como sempre saiu), e sem os textos em branco: o
 * servidor os recusaria, e eles não têm o que desenhar.
 */
export function pedidoDaFolha(folha: Folha): FolhaDeImpressao {
  return {
    layout: folha.layout,
    celulas: folha.celulas.map((c, i) => {
      if (ehEspacoDeMarcador(folha.layout, i)) {
        const m = folha.marcadores?.[i];
        return m ? { tipo: "marcador" as const, marcadorId: m.marcadorId, faixa: m.faixa } : null;
      }
      if (!c) return null;
      const camadas = semTextosEmBranco(c.camadas ?? []);
      return {
        geracaoId: c.fotoId,
        ...(c.fonte ? { fonte: c.fonte } : {}),
        enquadramento: c.enquadramento,
        ...(c.ampliada ? { imagemId: c.ampliada.id } : {}),
        ...(camadas.length > 0 ? { camadas } : {}),
      };
    }),
  };
}

export function quantasFotos(folha: Folha): number {
  return folha.celulas.filter((c) => c !== null).length;
}

/** O tamanho da imagem que a célula mostra: a ampliada, se houver, ou a versão da foto. */
export function tamanhoMostrado(celula: CelulaDaFolha, tamanhos: Tamanhos): Tamanho | undefined {
  return celula.ampliada?.tamanho ?? tamanhos[chaveDaFoto(celula.fotoId, celula.fonte)];
}

/**
 * A célula passa a usar a ampliação de cenário, e o enquadramento volta ao
 * padrão: a imagem nova tem outra proporção, e o recorte da antiga não serve.
 */
export function usarAmpliada(folha: Folha, indice: number, ampliada: AmpliacaoDaCelula): Folha {
  const atual = folha.celulas[indice];
  if (!atual) return folha;

  const celulas = [...folha.celulas];
  celulas[indice] = {
    fotoId: atual.fotoId,
    ...(atual.fonte ? { fonte: atual.fonte } : {}),
    ampliada,
    enquadramento: enquadramentoInicial(
      ampliada.tamanho,
      tamanhoDaCelula(folha.layout, indice),
      "preencher",
    ),
    ...(atual.camadas ? { camadas: atual.camadas } : {}),
  };
  return { ...folha, celulas };
}

/** Desfaz a ampliação: volta à foto publicada, com o enquadramento de partida. */
export function voltarAOriginal(folha: Folha, indice: number, tamanhos: Tamanhos): Folha {
  const atual = folha.celulas[indice];
  if (!atual?.ampliada) return folha;

  const celulas = [...folha.celulas];
  celulas[indice] = celulaCom(
    atual.fotoId,
    folha.layout,
    indice,
    tamanhos,
    "preencher",
    atual.fonte,
    atual.camadas,
  );
  return { ...folha, celulas };
}

// ─── Figurinhas, logos e textos ───────────────────────────────────────────────

/** A célula com estas camadas; sem nenhuma, sem a chave (nunca `camadas: []`). */
function comCamadas(celula: CelulaDaFolha, camadas: readonly Camada[]): CelulaDaFolha {
  const { camadas: _antigas, ...resto } = celula;
  return camadas.length > 0 ? { ...resto, camadas: [...camadas] } : resto;
}

/** Guarda as camadas da célula (o resultado do editor). Célula vazia não tem onde guardar. */
export function guardarCamadas(folha: Folha, indice: number, camadas: readonly Camada[]): Folha {
  const atual = folha.celulas[indice];
  if (!atual) return folha;
  const celulas = [...folha.celulas];
  celulas[indice] = comCamadas(atual, camadas);
  return { ...folha, celulas };
}

/** Quantas camadas a célula tem. */
export const quantasCamadas = (celula: CelulaDaFolha | null | undefined): number =>
  celula?.camadas?.length ?? 0;

/** As figurinhas que a folha cita, sem repetir: o que a tela precisa perguntar à biblioteca. */
export function idsDeFigurinhasDaFolha(folha: Folha): number[] {
  return idsDasFigurinhas(folha.celulas.flatMap((c) => c?.camadas ?? []));
}

/**
 * "Aplicar em todas as fotos": copia as camadas da célula `indice` para as outras células com foto (`aplicarEmTodas`,
 * de `@foto/shared`). `substituir` deixa as outras com **as mesmas** camadas — inclusive nenhuma, se a origem
 * não tem —; `acrescentar` põe as da origem por cima das que elas já têm, e a célula que passaria do limite
 * fica como estava e vem em `naoCouberam`. Células vazias ficam como estão, e o enquadramento de nenhuma muda.
 */
export function aplicarCamadasEmTodas(
  folha: Folha,
  indice: number,
  modo: "substituir" | "acrescentar",
): { folha: Folha; atualizadas: number[]; naoCouberam: number[] } {
  if (!folha.celulas[indice]) return { folha, atualizadas: [], naoCouberam: [] };

  const resultado = aplicarEmTodas(
    folha.celulas.map((c) => (c ? (c.camadas ?? []) : null)),
    indice,
    modo,
  );
  const atualizadas: number[] = [];
  const celulas = folha.celulas.map((c, i) => {
    if (c === null || i === indice || resultado.naoCouberam.includes(i)) return c;
    atualizadas.push(i);
    return comCamadas(c, resultado.celulas[i] ?? []);
  });
  return { folha: { ...folha, celulas }, atualizadas, naoCouberam: resultado.naoCouberam };
}

/**
 * Os espaços da folha com camada de imagem cuja figurinha não existe mais. O servidor recusa o pedido da folha
 * inteira enquanto houver uma, então a tela avisa e aponta onde está.
 */
export function celulasComImagemIndisponivel(
  folha: Folha,
  figurinhas: FigurinhasDasCamadas | undefined,
): number[] {
  return folha.celulas.flatMap((c, i) =>
    c?.camadas && camadasIndisponiveis(c.camadas, figurinhas).length > 0 ? [i] : [],
  );
}
